import api from '../api/axios';
import {
  AreaInvestigacion,
  DocenteOpcion,
  FiltrosCatalogo,
  GuiaOpcion,
  Pagina,
  Tema,
  TemaApp,
  TemaDetalle,
  TemaInput,
  TemaOpcion,
} from '../types/tema';

/** Deja fuera los filtros sin valor para no mandar `?area=&estado=` al backend. */
function sinVacios(filtros: FiltrosCatalogo): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  ) as Record<string, string | number>;
}

export const temaService = {
  /** Catálogo público: filtrado y paginado en el servidor, como el de la app. */
  list: async (filtros: FiltrosCatalogo = {}): Promise<Pagina<Tema>> => {
    const { data } = await api.get<Pagina<Tema>>('temas/', { params: sinVacios(filtros) });
    return data;
  },

  /** Catálogo de la app autenticada: filtrado y paginado en el servidor. */
  listApp: async (filtros: FiltrosCatalogo = {}): Promise<Pagina<TemaApp>> => {
    const { data } = await api.get<Pagina<TemaApp>>('temas/todos/', { params: sinVacios(filtros) });
    return data;
  },

  /** Profesores que ofrecen temas, para el filtro "Guía" (busca sin tildes ni eñes). */
  guias: async (q: string): Promise<GuiaOpcion[]> => {
    const { data } = await api.get<GuiaOpcion[]>('temas/guias/', { params: q ? { q } : undefined });
    return data;
  },

  /** Ficha de un tema. Sin sesión solo resuelve los temas disponibles. */
  get: async (id: number): Promise<TemaDetalle> => {
    const { data } = await api.get<TemaDetalle>(`temas/${id}/`);
    return data;
  },

  /** Crea un tema del catálogo (solo docentes/coordinador). Nace disponible. */
  create: async (input: TemaInput): Promise<TemaApp> => {
    const { data } = await api.post<TemaApp>('temas/todos/', input);
    return data;
  },

  /** Edita un tema (guía/coguía o coordinador). */
  update: async (id: number, input: TemaInput): Promise<TemaApp> => {
    const { data } = await api.patch<TemaApp>(`temas/${id}/`, input);
    return data;
  },

  /** Elimina un tema (no permitido si está asignado o con postulaciones). */
  remove: async (id: number): Promise<void> => {
    await api.delete(`temas/${id}/`);
  },

  /** Saca el tema del catálogo o lo devuelve (guía/coguía o coordinador). */
  pausar: async (id: number, pausar: boolean): Promise<TemaApp> => {
    const { data } = await api.post<TemaApp>(`temas/${id}/pausa/`, { pausar });
    return data;
  },

  /** Marca o desmarca el tema como favorito propio (la estrella). */
  favorito: async (id: number, favorito: boolean): Promise<TemaApp> => {
    const { data } = await api.post<TemaApp>(`temas/${id}/favorito/`, { favorito });
    return data;
  },

  /** Áreas de investigación (categorías) para el formulario de crear tema. */
  areas: async (): Promise<AreaInvestigacion[]> => {
    const { data } = await api.get<AreaInvestigacion[]>('areas/');
    return data;
  },

  /** Crea un área de investigación (solo coordinador). */
  crearArea: async (nombre: string): Promise<AreaInvestigacion> => {
    const { data } = await api.post<AreaInvestigacion>('areas/', { nombre });
    return data;
  },

  /** Borra un área (solo coordinador); se quita de los temas/docentes que la tenían. */
  /** Mete un área en una supercategoría (o la deja sin agrupar con `null`). */
  agruparArea: async (id: number, supercategoria: number | null): Promise<AreaInvestigacion> => {
    const { data } = await api.patch<AreaInvestigacion>(`areas/${id}/`, { supercategoria });
    return data;
  },

  borrarArea: async (id: number): Promise<void> => {
    await api.delete(`areas/${id}/`);
  },

  /** Docentes candidatos a coguía interno; búsqueda opcional por nombre. */
  /** `incluirYo`: la coordinación, al ponerle guía a un proceso, puede elegirse a sí misma. */
  docentes: async (q: string, incluirYo = false): Promise<DocenteOpcion[]> => {
    const params = { ...(q ? { q } : {}), ...(incluirYo ? { incluir_yo: 1 } : {}) };
    const { data } = await api.get<DocenteOpcion[]>('docentes/', { params });
    return data;
  },

  /** Temas como opciones para reasignar (coordinador); búsqueda por título. */
  opciones: async (q: string): Promise<TemaOpcion[]> => {
    const { data } = await api.get<TemaOpcion[]>('temas/opciones/', { params: q ? { q } : undefined });
    return data;
  },

  /** El coordinador le da cuenta al coguía externo del tema, para que pueda evaluar. */
  darAccesoCoguia: async (id: number): Promise<AccesoCoguia> => {
    const { data } = await api.post<AccesoCoguia>(`temas/${id}/acceso-coguia/`, {});
    return data;
  },
};

export interface AccesoCoguia {
  tema: Tema;
  persona: { nombre: string; email: string; rut: number | null };
  /** false cuando el correo ya era de alguien del sistema y se usó esa ficha. */
  creada: boolean;
  /** true si además se le creó la cuenta local (solo para quien no tiene RUT). */
  cuenta_creada: boolean;
}
